import { createContext, ReactElement, useReducer } from "react"
import axios from "utils/axios"

import {
  FETCH_AGREEMENT_UPLOADS,
  FETCH_CONFIRMED_OFFERS,
  FETCH_REPORT_UPLOAD_REQUESTS,
  FETCH_CURRENCY_EXCHANGE_RATES,
  FETCH_DEAL_OFFER_STATISTICS
} from "../store/reducers/actions"
import { mapPaginatedDataToCamelCase, mapToCamelCase, mapToSnakeCase } from "../utils/caseTransformation"
import { PaginatedData } from "types/root"
import {
  ReportsContextType,
  ConfirmedOffer,
  ConfirmedOfferTypes,
  ConfirmedOffersFilterType,
  AgreementUploadFilterType,
  AgreementUpload,
  ReportUploadRequest,
  ReportUploadRequestFilterType,
  CurrencyExchangeRate,
  DealOfferStatistics
} from "types/reports"
import reportsReducer, { initialReportsState } from "store/reducers/reports"
import { getFilterText } from "utils"
import { dispatch } from "store"

const ReportsContext = createContext<ReportsContextType | null>(null)

export const ReportsProvider = ({ children }: { children: ReactElement }) => {
  const [state, dispatch] = useReducer(reportsReducer, initialReportsState)

  const fetchConfirmedOfferList = async (
    limit: number,
    offset: number,
    isPublic: boolean,
    filter?: ConfirmedOffersFilterType,
    orderBy?: string,
    id?: number,
    userId?: number,
    isDealOffers?: boolean
  ) => {
    const idParam = id ? `&id=${id}` : ""
    const userIdParam = userId ? `&user_id=${userId}` : ""
    const orderByParam = orderBy ? `&order_by=${orderBy}` : ""

    const tagIncludedLength = filter?.tagsIncluded?.length || 0
    const tagExcludedLength = filter?.tagsExcluded?.length || 0
    const tagsIncludedParam = tagIncludedLength > 0 ? `&tags_included=${filter?.tagsIncluded.join(",")}` : ""
    const tagsExcludedParam = tagExcludedLength > 0 ? `&tags_excluded=${filter?.tagsExcluded.join(",")}` : ""
    const dealStatusParam = filter?.dealStatus ? `&deal_status=${filter.dealStatus}` : ""
    const filteredFilter = { ...filter, tagsIncluded: undefined, tagsExcluded: undefined, dealStatus: undefined }
    const isDealOffersParam = isDealOffers ? "&deal_offers=1" : ""

    const params = `?limit=${limit}&offset=${offset}&scouting=1${orderByParam}${idParam}${userIdParam}${getFilterText(
      filteredFilter
    )}${tagsIncludedParam}${tagsExcludedParam}${dealStatusParam}${isDealOffersParam}`

    const baseUrl = isPublic ? "/public/offers/confirmed_offers" : `/reports/confirmed-offers`
    const url = `${baseUrl}/${params}`
    const response = await axios(url, { method: "get" })

    dispatch({
      type: FETCH_CONFIRMED_OFFERS,
      payload: {
        ...state,
        paginatedConfirmedOfferList: mapPaginatedDataToCamelCase<PaginatedData<ConfirmedOffer>>(response.data)
      }
    })
  }

  const updateConfirmedOffer = async (
    id: number,
    approved: boolean,
    type: string,
    splitRate?: number,
    offer?: number
  ) => {
    const response = await axios(`/reports/confirmed-offers/save/`, {
      method: "put",
      data: {
        id,
        offer,
        approved,
        type,
        split_rate: splitRate
      }
    })

    if (!response.data.success) {
      return false
    }

    const results = [...state.paginatedConfirmedOfferList.results].map((confirmedOffer: ConfirmedOffer) => {
      if (confirmedOffer.id === id) {
        if (offer && confirmedOffer.type === ConfirmedOfferTypes.ADVANCE) {
          confirmedOffer.advanceOffer = offer
        } else if (offer && confirmedOffer.type === ConfirmedOfferTypes.BUYOUT) {
          confirmedOffer.buyoutOffer = offer
        }

        confirmedOffer.approved = approved
        confirmedOffer.type = type
        confirmedOffer.splitRate = splitRate
      }
      return confirmedOffer
    })

    dispatch({
      type: FETCH_CONFIRMED_OFFERS,
      payload: {
        ...state,
        paginatedConfirmedOfferList: { ...state.paginatedConfirmedOfferList, results }
      }
    })

    return true
  }

  const updateSongSelectionOffer = async (confirmedOfferId: number, songSelectionId: number, offer: number) => {
    const response = await axios(`/reports/confirmed-offers/song_selection_offer/`, {
      method: "put",
      data: { confirmed_offer_id: confirmedOfferId, song_selection_id: songSelectionId, offer_usd: offer }
    })

    if (!response.data.success) {
      return false
    }

    return true
  }

  const bulkUpdateSongSelectionOffer = async (
    confirmedOfferId: number,
    updates: { songSelectionId: number; offer: number }[]
  ) => {
    const response = await axios(`/reports/confirmed-offers/bulk_song_selection_offer/`, {
      method: "put",
      data: {
        confirmed_offer_id: confirmedOfferId,
        updates: updates.map((u) => ({ song_selection_id: u.songSelectionId, offer_usd: u.offer }))
      }
    })

    if (!response.data.success) {
      return false
    }

    return true
  }

  const saveNewVersionConfirmedOffer = async (id: number, type: string, offer: number) => {
    const response = await axios(`/reports/confirmed-offers/save_new_version/`, {
      method: "put",
      data: {
        id,
        offer,
        type
      }
    })

    if (!response.data.success) {
      return false
    }

    // Update local state with new offer value
    const results = [...state.paginatedConfirmedOfferList.results].map((confirmedOffer: ConfirmedOffer) => {
      if (confirmedOffer.id === id) {
        if (type === ConfirmedOfferTypes.ADVANCE) {
          confirmedOffer.advanceOffer = offer
        } else if (type === ConfirmedOfferTypes.BUYOUT) {
          confirmedOffer.buyoutOffer = offer
        }
        confirmedOffer.type = type
      }
      return confirmedOffer
    })

    dispatch({
      type: FETCH_CONFIRMED_OFFERS,
      payload: {
        ...state,
        paginatedConfirmedOfferList: { ...state.paginatedConfirmedOfferList, results }
      }
    })

    return true
  }

  const fetchAgreementUploadList = async (
    limit: number,
    offset: number,
    filter?: AgreementUploadFilterType,
    orderBy?: string
  ) => {
    const url = `/reports/royalty/agreement_uploads/?limit=${limit}&offset=${offset}&artist_id=${filter?.artistId}`

    const response = await axios(url, { method: "get" })

    dispatch({
      type: FETCH_AGREEMENT_UPLOADS,
      payload: {
        ...state,
        paginatedAgreementUploadList: mapPaginatedDataToCamelCase<PaginatedData<AgreementUpload>>(response.data)
      }
    })
  }

  const uploadAgreementFile = async (formData: any, artistId: number) => {
    const url = `/reports/royalty/upload_agreement_file/?artist_id=${artistId}`

    const response = await axios.post(url, formData)

    if (!response.data.result) {
      return false
    }

    const agreementUpload: AgreementUpload = mapToCamelCase(response.data.result)
    const results = [agreementUpload, ...state.paginatedAgreementUploadList.results]

    dispatch({
      type: FETCH_AGREEMENT_UPLOADS,
      payload: {
        ...state,
        paginatedAgreementUploadList: { ...state.paginatedAgreementUploadList, results }
      }
    })

    return true
  }

  const mergeConfirmedOffers = async (confirmedOfferIds: number[]) => {
    const idParam = confirmedOfferIds.join(",")
    const response = await axios(`/reports/confirmed-offers/merge/?offer_ids=${idParam}`, { method: "post" })

    if (!response.data.success) {
      return false
    }

    return true
  }

  const fetchReportUploadRequestList = async (
    limit: number,
    offset: number,
    filter?: ReportUploadRequestFilterType,
    orderBy?: string
  ) => {
    const url = `/reports/report-upload-request/?limit=${limit}&offset=${offset}${getFilterText(filter)}`

    const response = await axios(url, { method: "get" })

    dispatch({
      type: FETCH_REPORT_UPLOAD_REQUESTS,
      payload: {
        ...state,
        paginatedReportUploadRequestList: mapPaginatedDataToCamelCase<PaginatedData<ReportUploadRequest>>(response.data)
      }
    })
  }

  const fetchCurrencyExchangeRates = async () => {
    const url = `/reports/currency-exchange/`
    const response = await axios.get(url)

    if (!response.data.results) {
      return
    }

    const currencyExchangeRates: CurrencyExchangeRate = response.data.results

    dispatch({
      type: FETCH_CURRENCY_EXCHANGE_RATES,
      payload: {
        ...state,
        currencyExchangeRates
      }
    })
  }

  const fetchDealOffersStatistics = async (filter?: ConfirmedOffersFilterType) => {
    const response = await axios.get("/reports/deal-offers/statistics/", {
      params: mapToSnakeCase(filter)
    })

    dispatch({
      type: FETCH_DEAL_OFFER_STATISTICS,
      payload: {
        ...state,
        dealOfferStatistics: mapToCamelCase<DealOfferStatistics>(response.data)
      }
    })
  }

  return (
    <ReportsContext.Provider
      value={{
        ...state,
        fetchConfirmedOfferList,
        updateConfirmedOffer,
        saveNewVersionConfirmedOffer,
        updateSongSelectionOffer,
        bulkUpdateSongSelectionOffer,
        fetchAgreementUploadList,
        uploadAgreementFile,
        fetchReportUploadRequestList,
        fetchCurrencyExchangeRates,
        mergeConfirmedOffers,
        fetchDealOffersStatistics
      }}
    >
      {children}
    </ReportsContext.Provider>
  )
}

export default ReportsContext
